import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompaniesDto,
  OverviewDto,
  RegionsDto,
  RigDetailDto,
  RigsDto,
  RigsMapDto,
  SourcesDto,
  WellsDto,
} from '../types';

// The Phase-B live "Api" layer. Every WWD tab reads the WWD Api through the portal BFF
// reverse-proxy (/api/wwd/** -> WWD Api /api/**, X-Portal-Trusted attached server-side).
// Same DTOs as the retired Phase-A fixtures (types.ts is the contract), so graduation was
// exactly the promised fetch-layer swap — the tabs' render code didn't change shape.

export class WwdApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'WwdApiError';
  }
}

async function wwdFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/wwd/${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...init,
  });
  if (!res.ok) throw new WwdApiError(res.status, res.statusText || `HTTP ${res.status}`);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const STALE_MS = 60_000;

/** Don't retry 404s (e.g. unknown rig slug) — they won't get better. */
function retryUnlessNotFound(failureCount: number, err: unknown): boolean {
  const status = (err as { status?: number })?.status;
  if (status === 404) return false;
  return failureCount < 2;
}

// ── The 7 tab payloads ───────────────────────────────────────────────────────

export function useOverview() {
  return useQuery({
    queryKey: ['wwd', 'overview'],
    queryFn: () => wwdFetch<OverviewDto>('overview'),
    staleTime: STALE_MS,
  });
}

export function useWells() {
  return useQuery({
    queryKey: ['wwd', 'wells'],
    queryFn: () => wwdFetch<WellsDto>('wells'),
    staleTime: STALE_MS,
  });
}

export function useRigs() {
  return useQuery({
    queryKey: ['wwd', 'rigs'],
    queryFn: () => wwdFetch<RigsDto>('rigs'),
    staleTime: STALE_MS,
  });
}

/** Fleet-map markers — only rigs with a scraped AIS position (lightweight payload). */
export function useRigsMap() {
  return useQuery({
    // NOT ['wwd','rigs','map'] — that's the useRigDetail key shape for a slug 'map'.
    queryKey: ['wwd', 'rigs-map'],
    queryFn: () => wwdFetch<RigsMapDto>('rigs/map'),
    staleTime: STALE_MS,
  });
}

export function useRigDetail(slug: string | undefined) {
  return useQuery({
    queryKey: ['wwd', 'rigs', slug],
    queryFn: () => wwdFetch<RigDetailDto>(`rigs/${encodeURIComponent(slug ?? '')}`),
    enabled: slug !== undefined,
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
  });
}

export function useRegions() {
  return useQuery({
    queryKey: ['wwd', 'regions'],
    queryFn: () => wwdFetch<RegionsDto>('regions'),
    staleTime: STALE_MS,
  });
}

export function useCompanies() {
  return useQuery({
    queryKey: ['wwd', 'companies'],
    queryFn: () => wwdFetch<CompaniesDto>('companies'),
    staleTime: STALE_MS,
  });
}

export function useSources() {
  return useQuery({
    queryKey: ['wwd', 'sources'],
    queryFn: () => wwdFetch<SourcesDto>('sources'),
    staleTime: STALE_MS,
  });
}

// ── Admin / Debug (portal-admin only — the BFF adds X-Portal-Role: admin) ────

/** One row of the human review queue; id is "{kind}:{rowId}", e.g. "contracts:42". */
export interface ReviewItemDto {
  id: string;
  kind: string;
  summary: string;
  reviewNote: string | null;
  rigSlug: string | null;
  extractedDayrateUsd: number | null;
  url: string | null;
  flaggedAt: string;
}

export interface ReviewListDto {
  items: ReviewItemDto[];
}

export interface WwdQuartzJobDto {
  key: string;
  cron: string | null;
  previousFire: string | null;
  nextFire: string | null;
}

export interface TriggerResultDto {
  success: boolean;
  message: string;
}

export interface UsageProviderRowDto {
  provider: string;
  calls: number;
  costUsd: number;
  tokensIn: number;
  tokensOut: number;
}

export interface WwdUsageDto {
  month: string;
  totalCostUsd: number;
  totalCalls: number;
  providers: UsageProviderRowDto[];
}

const DEBUG_REFETCH_MS = 8_000;

export function useReviewQueue(enabled: boolean) {
  return useQuery({
    queryKey: ['wwd', 'admin', 'review'],
    queryFn: () => wwdFetch<ReviewListDto>('admin/review'),
    enabled,
  });
}

export function useReviewAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      wwdFetch<unknown>(`admin/review/${encodeURIComponent(id)}/${action}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwd', 'admin', 'review'] }),
  });
}

export function useWwdQuartz(enabled: boolean) {
  return useQuery({
    queryKey: ['wwd', 'admin', 'quartz'],
    queryFn: () => wwdFetch<WwdQuartzJobDto[]>('admin/debug/quartz'),
    enabled,
    refetchInterval: enabled ? DEBUG_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  });
}

export function useRunJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      wwdFetch<TriggerResultDto>(`admin/debug/triggers/${encodeURIComponent(key)}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwd', 'admin', 'quartz'] }),
  });
}

export function useWwdUsage(month: string, enabled: boolean) {
  return useQuery({
    queryKey: ['wwd', 'admin', 'usage', month],
    queryFn: () => wwdFetch<WwdUsageDto>(`admin/usage?month=${encodeURIComponent(month)}`),
    enabled,
  });
}

// ── Data API key management (admin-only; keys gate the public /api/data/wwd route) ────

export interface DataApiKeyRowDto {
  id: number;
  consumer: string;
  keyPrefix: string;
  note: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** False for revoked keys and keys minted before plaintext storage (2026-07-08). */
  canReveal: boolean;
}

export interface DataApiKeyListDto {
  keys: DataApiKeyRowDto[];
}

/** Creation response — the ONLY time the full key is visible. */
export interface DataApiKeyCreatedDto {
  id: number;
  consumer: string;
  key: string;
  keyPrefix: string;
  createdAt: string;
}

export function useDataApiKeys(enabled: boolean) {
  return useQuery({
    queryKey: ['wwd', 'admin', 'apikeys'],
    queryFn: () => wwdFetch<DataApiKeyListDto>('admin/apikeys'),
    enabled,
  });
}

export function useCreateDataApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ consumer, note }: { consumer: string; note?: string }) =>
      wwdFetch<DataApiKeyCreatedDto>('admin/apikeys', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ consumer, note: note || null }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwd', 'admin', 'apikeys'] }),
  });
}

/** Copy-anytime: fetch an active key's plaintext on demand (admin-gated reveal endpoint). */
export function revealDataApiKey(id: number) {
  return wwdFetch<{ id: number; consumer: string; key: string }>(`admin/apikeys/${id}/key`);
}

export function useRevokeDataApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      wwdFetch<unknown>(`admin/apikeys/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwd', 'admin', 'apikeys'] }),
  });
}

/** Hard delete — the API only accepts this for REVOKED keys (active ones must be revoked first). */
export function useDeleteDataApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      wwdFetch<unknown>(`admin/apikeys/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwd', 'admin', 'apikeys'] }),
  });
}

// ── Data API traffic dashboard (admin/dataapi — same gate as key management) ────

export type TrafficWindow = '24h' | '7d' | '30d';

export interface DataApiStatsTotalsDto {
  requests: number;
  errors: number;
  /** errors / requests in [0,1]; 0 when there is no traffic. */
  errorRate: number;
  activeConsumers: number;
  /** 401s — missing/invalid-key attempts (the abuse signal). */
  invalidAttempts: number;
  /** 429s — rate-limited requests. */
  rateLimited: number;
}

/** One time bucket: bucket start (ISO-8601 UTC) with request + error counts. Dense (zero-filled). */
export interface DataApiTrafficPointDto {
  bucketUtc: string;
  requests: number;
  errors: number;
}

export interface DataApiConsumerStatDto {
  consumer: string;
  requests: number;
  errors: number;
  lastSeenUtc: string | null;
  /** The "(invalid)"/"(method)" sentinel bucket for unauthenticated attempts. */
  invalid: boolean;
}

export interface DataApiEndpointStatDto {
  endpoint: string;
  requests: number;
  errors: number;
}

export interface DataApiStatsDto {
  window: string;
  generatedAtUtc: string;
  totals: DataApiStatsTotalsDto;
  series: DataApiTrafficPointDto[];
  consumers: DataApiConsumerStatDto[];
  endpoints: DataApiEndpointStatDto[];
}

export function useDataApiStats(window: TrafficWindow, enabled: boolean) {
  return useQuery({
    queryKey: ['wwd', 'admin', 'apikeys', 'stats', window],
    queryFn: () => wwdFetch<DataApiStatsDto>(`admin/apikeys/stats?window=${window}`),
    enabled,
    // Traffic is written per request; refresh often enough that a tester call shows up.
    refetchInterval: enabled ? 20_000 : false,
    refetchIntervalInBackground: false,
  });
}
